import { useEffect, useState } from "react";
import { api, formatEUR } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RestaurantChangePreview } from "../components/RestaurantChangePreview";

const empty = { name: "", city: "", contact_name: "", contact_email: "", notes: "", pax_per_session: 0, pax_min_per_session: 0, sessions_per_day: 1, price_per_pax: 0, iva_percent: 10, billing_base_percent: 90, billing_scheme: "standard" };

export default function Restaurants() {
  const { user } = useAuth();
  const isAdmin = ["admin", "editor", "operator"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/restaurants").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("El nombre es obligatorio");
    const payload = {
      ...form,
      pax_per_session: Number(form.pax_per_session) || 0,
      pax_min_per_session: Number(form.pax_min_per_session) || 0,
      sessions_per_day: Number(form.sessions_per_day) || 1,
      price_per_pax: Number(form.price_per_pax),
      iva_percent: Number(form.iva_percent),
      billing_base_percent: Number(form.billing_base_percent) || 90,
      billing_scheme: form.billing_scheme || "standard",
    };
    if (editingId) {
      try {
        const { data } = await api.post(`/restaurants/${editingId}/preview-changes`, payload);
        if (data.has_effects) return setPreview({ ...data, rid: editingId, payload });
      } catch (e) { return toast.error(e.response?.data?.detail || "Error"); }
    }
    persist(editingId, payload);
  };

  const persist = async (rid, payload) => {
    setSaving(true);
    try {
      if (rid) await api.put(`/restaurants/${rid}`, payload);
      else await api.post("/restaurants", payload);
      toast.success("Guardado");
      setPreview(null); setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const startEdit = (r) => {
    setForm({
      name: r.name, city: r.city || "", contact_name: r.contact_name || "",
      contact_email: r.contact_email || "", notes: r.notes || "",
      pax_per_session: r.pax_per_session || 0,
      pax_min_per_session: r.pax_min_per_session || 0,
      sessions_per_day: r.sessions_per_day || 1,
      price_per_pax: r.price_per_pax || 0, iva_percent: r.iva_percent || 10,
      billing_base_percent: r.billing_base_percent || 90,
      billing_scheme: r.billing_scheme || "standard",
    });
    setEditingId(r.id); setOpen(true);
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar ${r.name}?`)) return;
    await api.delete(`/restaurants/${r.id}`);
    toast.success("Eliminado"); load();
  };

  const byCity = items.reduce((acc, r) => {
    const c = r.city || "Sin ciudad";
    (acc[c] = acc[c] || []).push(r);
    return acc;
  }, {});

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="restaurants-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Cartera</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Restaurantes</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">Configura precio (IVA incl.), IVA aplicable, aforo por turno y nº típico de turnos por día.</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-restaurant-button" className="rounded-none bg-zinc-900 hover:bg-zinc-800">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> Nuevo restaurante
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{editingId ? "Editar" : "Nuevo"} restaurante</DialogTitle>
                <DialogDescription>Configura los datos del restaurante. El precio y aforo se usan para calcular las facturas.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nombre</Label>
                  <Input data-testid="restaurant-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Ciudad</Label>
                    <Input data-testid="restaurant-field-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Email contacto</Label>
                    <Input data-testid="restaurant-field-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-none" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nombre contacto</Label>
                  <Input data-testid="restaurant-field-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-none" />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Aforo máx/turno</Label>
                    <Input data-testid="restaurant-field-pax" type="number" step="0.01" value={form.pax_per_session} onChange={(e) => setForm({ ...form, pax_per_session: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Aforo mín/turno</Label>
                    <Input data-testid="restaurant-field-pax-min" type="number" step="0.01" value={form.pax_min_per_session} onChange={(e) => setForm({ ...form, pax_min_per_session: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Turnos/día</Label>
                    <Input data-testid="restaurant-field-sessions-per-day" type="number" min="1" value={form.sessions_per_day} onChange={(e) => setForm({ ...form, sessions_per_day: e.target.value })} className="rounded-none" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Precio (IVA)</Label>
                    <Input data-testid="restaurant-field-price" type="number" step="0.01" value={form.price_per_pax} onChange={(e) => setForm({ ...form, price_per_pax: e.target.value })} className="rounded-none" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider">IVA</Label>
                    <Select value={String(form.iva_percent)} onValueChange={(v) => setForm({ ...form, iva_percent: Number(v) })}>
                      <SelectTrigger className="rounded-none" data-testid="restaurant-field-iva"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="10">10%</SelectItem>
                        <SelectItem value="21">21%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Base de facturación (fallback)</Label>
                    <Select value={String(form.billing_base_percent)} onValueChange={(v) => setForm({ ...form, billing_base_percent: Number(v) })}>
                      <SelectTrigger className="rounded-none" data-testid="restaurant-field-base"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="90">90% del aforo</SelectItem>
                        <SelectItem value="95">95% del aforo</SelectItem>
                        <SelectItem value="100">100% del aforo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-1">El <strong>aforo mínimo</strong> es la base sobre la que se calculan anticipo (10%) y principal (90%). Los asistentes por encima del mínimo (hasta el máximo) se facturan como excedentes. Si el aforo mínimo está a 0 se usa el porcentaje "Base" sobre el aforo máximo.</p>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Esquema de facturación</Label>
                  <Select value={form.billing_scheme || "standard"} onValueChange={(v) => setForm({ ...form, billing_scheme: v })}>
                    <SelectTrigger className="rounded-none" data-testid="restaurant-field-scheme"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="standard">Estándar (10% anticipo + 90% principal)</SelectItem>
                      <SelectItem value="tatel_advance">Tatel (95% × aforo × evento, 15 días antes)</SelectItem>
                      <SelectItem value="amar_daily">Amar (10% anticipo + factura diaria por fecha)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-zinc-500 mt-1">Marca el esquema Tatel para restaurantes que se facturan 1 factura por evento al 95% del aforo máximo × precio, con vencimiento 15 días antes del evento. El esquema Amar emite el anticipo del 10% y después una factura por cada fecha: (asistentes reales, mínimo garantizado si van menos) × precio − anticipo ÷ nº de fechas, con vencimiento 15 días después.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Notas</Label>
                  <Input data-testid="restaurant-field-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-restaurant-button" onClick={save} disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800">Guardar</Button>
              </DialogFooter>
              <RestaurantChangePreview
                preview={preview}
                saving={saving}
                onCancel={() => setPreview(null)}
                onConfirm={() => persist(preview.rid, preview.payload)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-8">
        {Object.keys(byCity).length === 0 && (
          <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-500">No hay restaurantes. {isAdmin && "Importa el Excel desde Eventos o crea uno con el botón superior."}</div>
        )}
        {Object.entries(byCity).sort(([a],[b]) => a.localeCompare(b)).map(([city, rs]) => (
          <section key={city} data-testid={`city-section-${city}`}>
            <p className="label-eyebrow mb-3">{city}</p>
            <div className="bg-white border border-zinc-200">
              <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                <div className="col-span-3">Nombre</div>
                <div className="col-span-2">Aforo mín · máx</div>
                <div className="col-span-1">Turnos/día</div>
                <div className="col-span-2">Precio</div>
                <div className="col-span-1">IVA</div>
                <div className="col-span-1">Base</div>
                <div className="col-span-1">Contacto</div>
                <div className="col-span-1 text-right">Acciones</div>
              </div>
              {rs.map((r) => {
                const fmt = (n) => {
                  const v = Number(n || 0);
                  return Number.isInteger(v) ? v.toString() : v.toLocaleString("es-ES", { maximumFractionDigits: 2 });
                };
                const pmaxRaw = Number(r.pax_per_session || 0);
                const pminRaw = r.pax_min_per_session
                  ? Number(r.pax_min_per_session)
                  : pmaxRaw * (r.billing_base_percent || 90) / 100;
                const baseLabel = `${r.billing_base_percent || 90}%`;
                return (
                <div key={r.id} className="grid grid-cols-12 px-6 py-4 border-b border-zinc-100 items-center hover:bg-zinc-50" data-testid={`restaurant-row-${r.id}`}>
                  <div className="col-span-3">
                    <p className="font-medium" data-testid={`restaurant-name-${r.id}`}>{r.name}</p>
                  </div>
                  <div className="col-span-2 font-mono text-sm">
                    {pmaxRaw ? (
                      <span>{fmt(pminRaw)} · <span className="text-zinc-500">{fmt(pmaxRaw)}</span></span>
                    ) : (
                      <span className="text-amber-600">Sin definir</span>
                    )}
                  </div>
                  <div className="col-span-1 font-mono text-sm">{r.sessions_per_day || 1}</div>
                  <div className="col-span-2 font-mono text-sm">
                    {formatEUR(r.price_per_pax || 0)}
                    <span className="ml-1 text-[10px] uppercase tracking-wider text-zinc-500" data-testid={`iva-incl-${r.id}`}>IVA INCL.</span>
                  </div>
                  <div className="col-span-1 font-mono text-sm">{r.iva_percent}%</div>
                  <div className="col-span-1 font-mono text-xs" data-testid={`base-${r.id}`}>
                    <span className="font-semibold text-zinc-900">{baseLabel}</span>
                  </div>
                  <div className="col-span-1 text-sm text-zinc-600 truncate font-mono">{r.contact_email ? "✓" : "—"}</div>
                  <div className="col-span-1 flex justify-end gap-2">
                    {isAdmin && (
                      <>
                        <button onClick={() => startEdit(r)} data-testid={`edit-${r.id}`} className="text-zinc-600 hover:text-zinc-900"><Edit className="h-4 w-4" strokeWidth={1.5} /></button>
                        <button onClick={() => remove(r)} data-testid={`delete-${r.id}`} className="text-zinc-600 hover:text-red-600"><Trash2 className="h-4 w-4" strokeWidth={1.5} /></button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
